import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  Row,
  Table,
} from "reactstrap";
import Select from "react-select";
import { PatternFormat } from "react-number-format";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getClientRegisterDataById,
  getClientRegisterDocumentRequiredRows,
  getClientRegisterJobRequireRows,
  getCommonClientCompanyOptions,
  getCommonClientSourceOptions,
  getCommonInterviewLinkOptions,
  getCommonJobCategoryOptions,
  getCommonPersonalityTestLinkOptions,
  getCommonPlanOptions,
  getCommonTimezoneOptions,
  getCommonTwilioPhoneOptions,
  mapCommonPlanApiToSelectOptions,
  upsertClientRegisterMultipart,
} from "model/ClientRegisterModel";
import { fetchCommonJobOptions } from "model/JobModel";
import { toPhoneDigits, urlEndPoint, US_PHONE_INPUT_FORMAT } from "helpers/GeneralHelper";
import Loaders from "components/Common/Loaders";

const selectZIndexStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
};

const JOB_OPTIONS = [
  "Non CDL/L20",
  "Amazon Delivery Driver",
  "L10",
  "Weekend L20 Driver",
  "Lead Driver - BC",
  "AVP",
  "General Mechanic",
  "BC - Manager",
  "Bulk Truck (L20)",
  "Driver Helper",
  "L20 - L20 (Contingency)",
  "Existing Fedex Driver",
  "Seasonal L20 Driver",
  "Fedex Dispatcher",
  "AVP - Contractor's Vehicle",
  "Lead Driver",
  "Large Package Driver",
];

const WEEK_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri"];

/** e.g. user@gmail.com, user@ancile.services */
const EMAIL_REGEX = /^[\w-.]+@([\w-]+\.)+[\w-]{2,}$/i;

const isValidEmail = (value) => EMAIL_REGEX.test(String(value ?? "").trim());

const areValidEmails = (value) =>
  String(value ?? "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
    .every(isValidEmail);

// API/hydration stores duplicate keys; form inputs update only one alias.
const SYNCED_FORM_FIELDS = {
  facilityId: ["facilityID"],
  facilityID: ["facilityId"],
  billingName: ["billName"],
  billName: ["billingName"],
  billingEmail: ["billEmail"],
  billEmail: ["billingEmail"],
  billingPhone: ["billPhone"],
  billPhone: ["billingPhone"],
  accountNumber: ["fadvAccount"],
  fadvAccount: ["accountNumber"],
  password: ["fadvPassword"],
  fadvPassword: ["password"],
  secret: ["fadvSecret"],
  fadvSecret: ["secret"],
  clientBUcode: ["clientBUCode"],
  clientBUCode: ["clientBUcode"],
  smsToClient: ["sendSMS"],
  sendSMS: ["smsToClient"],
  smsToCandidate: ["sendSMSCandidate"],
  sendSMSCandidate: ["smsToCandidate"],
  howManyRoutes: ["routeCount"],
  routeCount: ["howManyRoutes"],
  billAddress: ["address"],
  address: ["billAddress"],
};

const toStringArray = (value) =>
  (Array.isArray(value) ? value : [])
    .map((item) => String(item ?? "").trim())
    .filter(Boolean);

const resolveJobIdsFromRow = (row, fallback = []) => {
  if (Array.isArray(row?.jobIds)) return toStringArray(row.jobIds);
  if (Array.isArray(row?.selectedJobs)) return toStringArray(row.selectedJobs);
  if (typeof row?.jobIds === "string") return toStringArray(row.jobIds.split(","));
  return fallback;
};

const resolveDownloadUrl = (raw = "") => {
  const value = String(raw || "").trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  const base = process.env.REACT_APP_LINK_URL || process.env.REACT_APP_ASSETS_URL || "";
  return `${base}${value.startsWith("/") ? value : `/${value}`}`;
};

const unwrapDocumentRequiredRows = (response) => {
  const raw = response?.data;
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.records)) return raw.records;
  if (Array.isArray(raw?.data)) return raw.data;
  return [];
};

const mapDocumentRequiredApiRow = (row = {}) => {
  const documentId = String(row?.documentId ?? "").trim();
  const documentName = String(row?.documentName ?? "").trim();
  if (!documentId || !documentName) return null;

  return {
    id: row?.id ?? null,
    documentId,
    documentName,
    documentStatus: String(row?.documentStatus ?? "active").trim(),
    isActive: Boolean(row?.isActive),
    documentFileId: row?.documentFileId ?? null,
    documentFile: null,
    existingFileUrl: resolveDownloadUrl(row?.documentFileUrl),
  };
};

const buildDocumentRequiredFromApi = (apiRows = []) =>
  (Array.isArray(apiRows) ? apiRows : [])
    .filter((item) => String(item?.documentStatus ?? "active").toLowerCase() === "active")
    .map(mapDocumentRequiredApiRow)
    .filter(Boolean);

const unwrapJobRequireRows = (response) => {
  const raw = response?.data;
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.records)) return raw.records;
  if (Array.isArray(raw?.data)) return raw.data;
  return [];
};

const mapJobRequireApiRow = (row = {}) => {
  const jobRequireId = String(row?.jobRequireId ?? "").trim();
  const type = String(row?.jobRequireName ?? "").trim();
  if (!jobRequireId || !type) return null;

  return {
    id: row?.id ?? null,
    jobRequireId,
    type,
    sort: row?.sort ?? 99999,
    tool: Boolean(row?.requireForTool),
    client: Boolean(row?.requireForClient),
    optional: Boolean(row?.isOptional),
  };
};

const buildJobRequireFromApi = (apiRows = []) =>
  (Array.isArray(apiRows) ? apiRows : []).map(mapJobRequireApiRow).filter(Boolean);

const normalizeCommonList = (response) =>
  response?.data?.records || response?.data?.data || response?.data || response || [];

const toOption = (item = {}, fallback = "Option") => ({
  value: String(item?.id).trim(),
  label:
    item?.display,
});

const initialFormData = {
  id: null,
  jobCategoryId: null,
  questionTypeId: null,
  selectedJobs: [],
  jobIds: [],
  plan: null,
  planId: null,
  clientCompanyId: null,
  companyId: null,
  clientName: null,
  interviewLink: null,
  contactName: null,
  recConName: null,
  recruiterName: null,
  contactEmail: null,
  recConEmail: null,
  recruiterEmail: null,
  contactPhone: null,
  recConPhone: null,
  recruiterPhone: null,
  personalityTestLink: null,
  timeZone: null,
  offsetMinutes: 0,
  twilioPhoneNumberId: null,
  twilioPhoneId: null,
  billName: null,
  billingName: null,
  billEmail: null,
  billingEmail: null,
  billPhone: null,
  billingPhone: null,
  billAddress: null,
  entityId: null,
  howManyRoutes: null,
  routeCount: null,
  address: null,
  terminalAddress: null,
  fadvAccount: null,
  accountNumber: null,
  fadvLoginId: null,
  fadvUser: null,
  loginId: null,
  fadvPassword: null,
  password: null,
  fadvSecret: null,
  secret: null,
  sendEmails: [],
  emails: null,
  reportEmails: null,
  hiredOnboardingFromEmail: "noreply@funnl.team",
  clientSourceId: null,
  clientBUcode: null,
  clientBUCode: null,
  facilityID: null,
  facilityId: null,
  sendSMS: true,
  sendSMSCandidate: true,
  reportsInProgress: false,
  reportsClientReport: false,
  smsToClient: true,
  smsToCandidate: true,
  incentiveTargetMin: 0,
  incentiveTargetMax: 0,
  incentivePerAmount: 0,
  incentiveNegative: false,
  maxQuota: 0,
//   quotaStartDate: null,
  status: "active",
  isAIRecruiterEnabled: false,
//   billingDate: "",
  isRegister: null,
  reportSends: null,
  inProgresMailInterval: null,
  permalink: null,
  ttaid: 0,
  dynamicQues: null,
  mecPaperwork: 0,
  mecPaperworkFile: 0,
  jobDescription: null,
  sendCandidateLink: false,
  sendWellcomeSMS: 0,
  sendFADVSMS: 0,
  sendBackgroundSMS: 0,
  sendDrugScreeningSMS: 0,
  sendMECSMS: 0,
  inProgressMailInterval: [],
  drugPassMailInterval: [],
  complianceMailInterval: [],
  clientReportMailInterval: [],
  documentRequired: [],
  smsSettings: {
    welcomeSms: false,
    fadvSms: false,
    backgroundSms: false,
    drugScreeningSms: false,
    mecSms: false,
    aiRecruiterEnabled: false,
  },
  specialRequirements: [],
};

const ClientRegisterAddEditModal = ({
  setCompanyAddEditModalOpen,
  companyAddEditModalOpen,
  getCompanyList,
  itemId: itemIdFromProps,
  viewMode: viewModeFromProps = false,
  copyMode: copyModeFromProps = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const itemId = itemIdFromProps || location?.state?.itemId || null;
  const isViewMode = Boolean(viewModeFromProps || location?.state?.viewMode);
  const isCopyMode = Boolean(copyModeFromProps || location?.state?.copyMode);
  const isModalMode =
    typeof companyAddEditModalOpen === "boolean" &&
    typeof setCompanyAddEditModalOpen === "function";
  const isOpen = isModalMode ? companyAddEditModalOpen : true;

  const [formKey, setFormKey] = useState(0);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [jobCategoryOptions, setJobCategoryOptions] = useState([]);
  const [jobOptions, setJobOptions] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [interviewLinkOptions, setInterviewLinkOptions] = useState([]);
  const [personalityOptions, setPersonalityOptions] = useState([]);
  const [timezoneOptions, setTimezoneOptions] = useState([]);
  const [twilioOptions, setTwilioOptions] = useState([]);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [planOptions, setPlanOptions] = useState([]);
  const [documentRequiredLoading, setDocumentRequiredLoading] = useState(false);
  const [clientDataLoading, setClientDataLoading] = useState(false);
  const [interviewLinkLoading, setInterviewLinkLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [planChangeTooltipMessage, setPlanChangeTooltipMessage] = useState("");
  const formTopRef = useRef(null);
  const planChangeTooltipTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (planChangeTooltipTimeoutRef.current) {
        clearTimeout(planChangeTooltipTimeoutRef.current);
      }
    };
  }, []);

  const showPlanChangeTooltip = () => {
    if (planChangeTooltipTimeoutRef.current) {
      clearTimeout(planChangeTooltipTimeoutRef.current);
    }
    setPlanChangeTooltipMessage(
      "Changing the plan will automatically update the minimum and maximum incentive targets"
    );
    planChangeTooltipTimeoutRef.current = setTimeout(() => {
      setPlanChangeTooltipMessage("");
      planChangeTooltipTimeoutRef.current = null;
    }, 4000);
  };

  const fetchDocumentRequiredOptions = useCallback(async (registerId = null) => {
    const response = await getClientRegisterDocumentRequiredRows(
      registerId ? { Id: registerId } : {}
    );
    return unwrapDocumentRequiredRows(response);
  }, []);

  const fetchJobRequireOptions = useCallback(async (registerId = null) => {
    const response = await getClientRegisterJobRequireRows(
      registerId ? { Id: registerId } : {}
    );
    return unwrapJobRequireRows(response);
  }, []);

  const selectedJobIdSet = useMemo(
    () => new Set(toStringArray(formData?.selectedJobs)),
    [formData?.selectedJobs]
  );

  const updateFormField = (field, value) => {
    if (isViewMode) return;
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      (SYNCED_FORM_FIELDS[field] || []).forEach((alias) => {
        next[alias] = value;
      });
      return next;
    });
    if (errors?.[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const getSelectedOption = (options, value) =>
    options.find((option) => String(option?.value) === String(value)) || null;

  const closeModal = () => {
    if (isModalMode) {
      setCompanyAddEditModalOpen(false);
      return;
    }
    navigate(`/${urlEndPoint.clientRegister}`);
  };

  const handleInputChange = (e) => {
    if (isViewMode) return;
    const { name, value, type, checked } = e.target;
    updateFormField(name, type === "checkbox" ? checked : value);
  };

  const handleContactPhoneValueChange = (values) => {
    if (isViewMode) return;
    updateFormField("contactPhone", toPhoneDigits(values?.value || ""));
  };

  const handleBillingPhoneValueChange = (values) => {
    if (isViewMode) return;
    updateFormField("billingPhone", toPhoneDigits(values?.value || ""));
  };

  /** PatternFormat truncates to 10 digits before onValueChange; strip +1 on paste first. */
  const handlePhonePaste = (fieldName) => (e) => {
    if (isViewMode) return;
    const pasted = e.clipboardData?.getData("text") || "";
    const digits = String(pasted).replace(/\D/g, "");
    if (digits.length === 11 && digits.startsWith("1")) {
      e.preventDefault();
      updateFormField(fieldName, toPhoneDigits(pasted));
    }
  };

  const handleDayToggle = (fieldName, day) => {
    if (isViewMode) return;
    const selectedDays = formData?.[fieldName] || [];
    const nextValue = selectedDays.includes(day)
      ? selectedDays.filter((item) => item !== day)
      : [...selectedDays, day];
    updateFormField(fieldName, nextValue);
  };

  const toggleSpecialRequirement = (index, key) => {
    if (isViewMode) return;
    setFormData((prev) => ({
      ...prev,
      specialRequirements: prev.specialRequirements.map((item, idx) =>
        idx === index ? { ...item, [key]: !item[key] } : item
      ),
    }));
  };

  const toggleDocumentRequired = (index) => {
    if (isViewMode) return;
    setFormData((prev) => ({
      ...prev,
      documentRequired: prev.documentRequired.map((item, idx) =>
        idx === index ? { ...item, isActive: !item.isActive } : item
      ),
    }));
  };

  const handleDocumentFileChange = (index, file) => {
    if (isViewMode) return;
    setFormData((prev) => ({
      ...prev,
      documentRequired: prev.documentRequired.map((item, idx) =>
        idx === index
          ? { ...item, documentFile: file || null, existingFileUrl: item?.existingFileUrl || "" }
          : item
      ),
    }));
  };

  const buildJobRequireDetailsForSubmit = () =>
    (formData?.specialRequirements || []).map((item) => ({
      id: isCopyMode ? null : item?.id ?? null,
      jobRequireId: item?.jobRequireId ?? null,
      jobRequireName: item?.type ?? null,
      sort: item?.sort ?? 99999,
      requireForTool: Boolean(item?.tool),
      requireForClient: Boolean(item?.client),
      isOptional: Boolean(item?.optional),
    }));

  const buildClientDocumentRequiredsForSubmit = () =>
    (formData?.documentRequired || []).map((item) => ({
      id: isCopyMode ? null : item?.id ?? null,
      documentId: item?.documentId ?? null,
      documentFileId: item?.documentFileId ?? null,
      isActive: Boolean(item?.isActive),
      documentFile: item?.documentFile instanceof File ? item.documentFile : null,
    }));

  const validateForm = () => {
    const nextErrors = {};
    if (!formData?.jobCategoryId) nextErrors.jobCategoryId = "Job category is required";
    if (!formData?.selectedJobs?.length) nextErrors.selectedJobs = "At least one job is required";
    if (!(formData?.clientCompanyId || formData?.companyId)) nextErrors.companyId = "Company is required";
    if (!formData?.clientName?.trim()) nextErrors.clientName = "Client name is required";
    if (!(formData?.contactName || "").trim()) nextErrors.contactName = "Name is required";
    if (!(formData?.contactEmail || "").trim()) nextErrors.contactEmail = "Email is required";
    else if (!isValidEmail(formData?.contactEmail)) nextErrors.contactEmail = "Invalid email";
    const contactPhoneDigits = toPhoneDigits(formData?.contactPhone || "");
    if (!contactPhoneDigits) nextErrors.contactPhone = "Phone is required";
    else if (!/^\d{10}$/.test(contactPhoneDigits)) {
      nextErrors.contactPhone = "Phone number must be 10 digits";
    }
    if (!formData?.billingName?.trim()) nextErrors.billingName = "Billing name is required";
    if (!formData?.billingEmail?.trim()) nextErrors.billingEmail = "Billing email is required";
    else if (!isValidEmail(formData?.billingEmail)) nextErrors.billingEmail = "Invalid email";
    const billingPhoneDigits = toPhoneDigits(formData?.billingPhone || "");
    if (!billingPhoneDigits) nextErrors.billingPhone = "Billing phone is required";
    else if (!/^\d{10}$/.test(billingPhoneDigits)) {
      nextErrors.billingPhone = "Phone number must be 10 digits";
    }
    if (!formData?.entityId?.trim()) nextErrors.entityId = "Entity ID is required";
    if (!(formData?.howManyRoutes || "").trim()) nextErrors.howManyRoutes = "Route count is required";
    if (!(formData?.billAddress || "").trim()) nextErrors.billAddress = "Address is required";
    if (!formData?.terminalAddress?.trim()) nextErrors.terminalAddress = "Terminal address is required";
    if (!(formData?.emails || "").trim()) nextErrors.emails = "Emails is required";
    else if (!areValidEmails(formData?.emails)) nextErrors.emails = "Enter a valid email";
    if (!formData?.clientSourceId) nextErrors.clientSourceId = "Client source is required";
    if (!(formData?.facilityId || formData?.facilityID || "").trim()) {
      nextErrors.facilityId = "FacilityID is required";
    }
    if (!formData?.timeZone) nextErrors.timeZone = "TimeZone is required";
    if (!formData?.reportsInProgress && !formData?.reportsClientReport) {
      nextErrors.reportSends = "Reports send is required";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const parseIntervalValue = (value) => {
    if (Array.isArray(value)) return value;
    const raw = String(value || "");
    if (!raw.trim()) return [];
    return raw
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const asNull = (value) => {
    if (value === undefined || value === null) return null;
    if (typeof value === "string" && !value.trim()) return null;
    return value;
  };

  const boolOrNull = (value) => {
    if (value === undefined || value === null) return null;
    return Boolean(value);
  };

  const oneOrNull = (value) => (value ? 1 : null);

  const buildPayload = () => {
    const reportSends = [
      formData?.reportsInProgress ? "InProgress" : "",
      formData?.reportsClientReport ? "ClientReport" : "",
    ].filter(Boolean);

    const sendEmails = Array.isArray(formData?.sendEmails) && formData?.sendEmails?.length
      ? formData.sendEmails
      : String(formData?.emails || formData?.reportEmails || "")
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean);

    return {
      Id: isCopyMode ? null : asNull(formData?.id || itemId),
      JobCategoryId: asNull(formData?.jobCategoryId),
      JobIds: toStringArray(formData?.selectedJobs || formData?.jobIds),
      Plan: asNull(formData?.plan || formData?.planId),
      ClientCompanyId: asNull(formData?.clientCompanyId || formData?.companyId),
      ClientName: asNull(formData?.clientName),
      InterviewLink: asNull(formData?.interviewLink),
      ContactName: asNull(formData?.contactName || formData?.recConName || formData?.recruiterName),
      ContactEmail: asNull(formData?.contactEmail || formData?.recConEmail || formData?.recruiterEmail),
      ContactPhone: asNull(
        toPhoneDigits(formData?.contactPhone || formData?.recConPhone || formData?.recruiterPhone) || null
      ),
      PersonalityTestLink: asNull(formData?.personalityTestLink),
      TimeZone: asNull(formData?.timeZone),
      TwilioNumberId: asNull(formData?.twilioPhoneId || formData?.twilioPhoneNumberId),
      BillName: asNull(formData?.billName || formData?.billingName),
      BillEmail: asNull(formData?.billEmail || formData?.billingEmail),
      BillPhone: asNull(toPhoneDigits(formData?.billPhone || formData?.billingPhone) || null),
      EntityId: asNull(formData?.entityId),
      HowManyRoutes: asNull(formData?.howManyRoutes || formData?.routeCount),
      BillAddress: asNull(formData?.billAddress || formData?.address),
      TerminalAddress: asNull(formData?.terminalAddress),
      FADVAccount: asNull(formData?.fadvAccount || formData?.accountNumber),
      FADVLoginId: asNull(formData?.fadvLoginId || formData?.fadvUser || formData?.loginId),
      FADVPassword: asNull(formData?.fadvPassword || formData?.password),
      FADVSecret: asNull(formData?.fadvSecret || formData?.secret),
      Emails: asNull(formData?.emails || sendEmails?.join(",")),
      HiredOnboardingFromEmail: asNull(formData?.hiredOnboardingFromEmail),
      ClientSourceId: asNull(formData?.clientSourceId),
      ClientBUcode: asNull(formData?.clientBUcode || formData?.clientBUCode),
      FacilityId: asNull(formData?.facilityID || formData?.facilityId),
      ReportSends: reportSends,
      SendSMS: boolOrNull(formData?.sendSMS ?? formData?.smsToClient),
      SendSMSCandidate: boolOrNull(formData?.sendSMSCandidate ?? formData?.smsToCandidate),
      SendCandidateLink: formData?.sendCandidateLink ? 1 : 0,
      InProgresMailInterval: asNull((formData?.inProgressMailInterval || []).join(",")),
      DrugPassMailInterval: asNull((formData?.drugPassMailInterval || []).join(",")),
      ComplianceMailInterval: asNull((formData?.complianceMailInterval || []).join(",")),
      ClientReportMailInterval: asNull((formData?.clientReportMailInterval || []).join(",")),
      SendWellcomeSMS: oneOrNull(formData?.smsSettings?.welcomeSms),
      SendFADVSMS: oneOrNull(formData?.smsSettings?.fadvSms),
      SendBackgroundSMS: oneOrNull(formData?.smsSettings?.backgroundSms),
      SendDrugScreeningSMS: oneOrNull(formData?.smsSettings?.drugScreeningSms),
      SendMECSMS: oneOrNull(formData?.smsSettings?.mecSms),
      IsAIRecruiterEnabled: boolOrNull(
        formData?.smsSettings?.aiRecruiterEnabled ?? formData?.isAIRecruiterEnabled
      ),
      Permalink: asNull(formData?.permalink),
      Ttaid: Number(formData?.ttaid || 0) || null,
      DynamicQues: asNull(formData?.dynamicQues),
      MECPaperwork: 0,
      MECPaperworkFile: 0,
      JobDescription: asNull(formData?.jobDescription),
    };
  };

  const appendFormDataValue = (fd, key, value) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      fd.append(key, value.join(","));
      return;
    }
    fd.append(key, value);
  };

  const buildMultipartFormDataFromPayload = () => {
    const payload = buildPayload();
    const fd = new FormData();
    const jobIds = toStringArray(payload.JobIds);
    const reportSends = toStringArray(payload.ReportSends);

    Object.entries(payload).forEach(([key, value]) => {
      if (key === "JobIds" || key === "ReportSends") return;
      appendFormDataValue(fd, key, value);
    });

    jobIds.forEach((jobId) => {
      fd.append("JobIds", jobId);
    });

    reportSends.forEach((reportSend) => {
      fd.append("ReportSends", reportSend);
    });

    buildClientDocumentRequiredsForSubmit().forEach((item, index) => {
      const prefix = `ClientDocumentRequireds[${index}]`;
      if (item?.id != null && String(item.id).trim()) {
        fd.append(`${prefix}.id`, String(item.id));
      }
      if (item?.documentId != null && String(item.documentId).trim()) {
        fd.append(`${prefix}.documentId`, String(item.documentId));
      }
      if (item?.documentFileId != null && String(item.documentFileId).trim()) {
        fd.append(`${prefix}.documentFileId`, String(item.documentFileId));
      }
      fd.append(`${prefix}.isActive`, item?.isActive ? "true" : "false");
      if (item?.documentFile instanceof File) {
        fd.append(`${prefix}.documentFile`, item.documentFile);
      }
    });

    buildJobRequireDetailsForSubmit().forEach((item, index) => {
      const prefix = `JobRequireDetails[${index}]`;
      if (item?.id != null && String(item.id).trim()) {
        fd.append(`${prefix}.id`, String(item.id));
      }
      if (item?.jobRequireId != null && String(item.jobRequireId).trim()) {
        fd.append(`${prefix}.jobRequireId`, String(item.jobRequireId));
      }
      if (item?.jobRequireName != null && String(item.jobRequireName).trim()) {
        fd.append(`${prefix}.jobRequireName`, String(item.jobRequireName));
      }
      fd.append(`${prefix}.sort`, String(item?.sort ?? 99999));
      fd.append(`${prefix}.requireForTool`, item?.requireForTool ? "true" : "false");
      fd.append(`${prefix}.requireForClient`, item?.requireForClient ? "true" : "false");
      fd.append(`${prefix}.isOptional`, item?.isOptional ? "true" : "false");
    });
    return fd;
  };

  const scrollToFormTop = () => {
    formTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const getSavedClientId = (response) => {
    const data = response?.data;
    if (data != null && typeof data !== "object") {
      return String(data);
    }
    return (
      data?.id ||
      data?.Id ||
      data?.clientId ||
      data?.ClientId ||
      (isCopyMode ? "" : formData?.id || itemId || "")
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isViewMode) return;
    if (!validateForm()) {
      if (!formData?.reportsInProgress && !formData?.reportsClientReport) {
        toast.error("Reports send is required");
      }
      scrollToFormTop();
      return;
    }
    setSubmitting(true);
    const payload = buildMultipartFormDataFromPayload();
    const response = await upsertClientRegisterMultipart(payload);
    setSubmitting(false);

    if (response?.success) {
      toast.success(response?.message || "Client register saved successfully");
      const clientId = getSavedClientId(response);
      const jobIds = toStringArray(formData?.selectedJobs || formData?.jobIds);
      const jobDescriptionNavigationState = {
        sequentialJobFlow: true,
        jobIds,
      };
      if (isModalMode) {
        closeModal();
        if (clientId) {
          navigate(`/${urlEndPoint.jobDescriptionAddEdit}/${clientId}/new`, {
            state: jobDescriptionNavigationState,
          });
          return;
        }
        getCompanyList?.();
        return;
      }
      if (clientId) {
        navigate(`/${urlEndPoint.jobDescriptionAddEdit}/${clientId}/new`, {
          state: jobDescriptionNavigationState,
        });
        return;
      }
      navigate(`/${urlEndPoint.clientRegister}`);
    } else {
      toast.error(response?.message || "Unable to save client register");
    }
  };

  const fetchInterviewLinkOptions = async () => {
    setInterviewLinkLoading(true);
    try {
      const interviewLinkRes = await getCommonInterviewLinkOptions();
      setInterviewLinkOptions(
        (Array.isArray(normalizeCommonList(interviewLinkRes))
          ? normalizeCommonList(interviewLinkRes)
          : []
        )
          .map((item) => toOption(item, "Interview Link"))
          .filter((item) => item?.value)
      );
    } finally {
      setInterviewLinkLoading(false);
    }
  };

  const fetchDropdownData = async () => {
    const interviewLinkPromise = fetchInterviewLinkOptions();

    const [
      jobCategoryRes,
      companyRes,
      sourceRes,
      twilioRes,
      timezoneRes,
      personalityRes,
      planRes,
    ] = await Promise.all([
      getCommonJobCategoryOptions(),
      getCommonClientCompanyOptions(),
      getCommonClientSourceOptions(),
      getCommonTwilioPhoneOptions(),
      getCommonTimezoneOptions(),
      getCommonPersonalityTestLinkOptions(),
      getCommonPlanOptions(),
      interviewLinkPromise,
    ]);

    setJobCategoryOptions(mapCommonPlanApiToSelectOptions(jobCategoryRes));
    // Do not call setJobOptions([]) here: this runs in parallel with hydrateEditData when the modal
    // opens and would race, finishing after fetchJobsByJobCategoryId and clearing the job list.
    setCompanyOptions(
      (Array.isArray(normalizeCommonList(companyRes))
        ? normalizeCommonList(companyRes)
        : []
      )
        .map((item) => ({
        value: String(item?.id ?? item?._id ?? item?.value ?? "").trim(),
        label:
          item?.display ||
          item?.company ||
          item?.client ||
          item?.name ||
          item?.value ||
          "Company",
      }))
        .filter((item) => item?.value)
    );

    setTwilioOptions(
      (Array.isArray(normalizeCommonList(twilioRes))
        ? normalizeCommonList(twilioRes)
        : []
      )
        .map((item) => ({
          value: String(item?.id ?? item?.value ?? item?._id ?? "").trim(),
          label:
            item?.display ||
            item?.phone ||
            item?.number ||
            item?.name ||
            item?.value ||
            "Twilio Phone",
        }))
        .filter((item) => item?.value)
    );

    setTimezoneOptions(
      (Array.isArray(normalizeCommonList(timezoneRes))
        ? normalizeCommonList(timezoneRes)
        : []
      )
        .map((item) => ({
          value: String(item?.value || "").trim(),
          label: `${item?.data || "Timezone"} (${item?.value || ""})`,
        }))
        .filter((item) => item?.value)
    );

    const mappedPersonality = (
      Array.isArray(normalizeCommonList(personalityRes))
        ? normalizeCommonList(personalityRes)
        : []
    )
      .map((item) => toOption(item, "Personality Test Link"))
      .filter((item) => item?.value);
    setPersonalityOptions(mappedPersonality.length ? mappedPersonality : []);
    setSourceOptions(mapCommonPlanApiToSelectOptions(sourceRes));
    setPlanOptions(mapCommonPlanApiToSelectOptions(planRes));
  };

  useEffect(() => {
    if (!isOpen) return;
    fetchDropdownData();
  }, [isOpen]);

  useEffect(() => {
    const hydrateEditData = async () => {
      if (!isOpen) return;
      setDocumentRequiredLoading(true);
      if (itemId) setClientDataLoading(true);
      try {
      if (!itemId) {
        const [apiRows, jobRequireRows] = await Promise.all([
          fetchDocumentRequiredOptions(),
          fetchJobRequireOptions(),
        ]);
        setFormData({
          ...initialFormData,
          documentRequired: buildDocumentRequiredFromApi(apiRows),
          specialRequirements: buildJobRequireFromApi(jobRequireRows),
        });
        setJobOptions([]);
        setErrors({});
        setFormKey((k) => k + 1);
        return;
      }
      const [response, apiRows, jobRequireRows] = await Promise.all([
        getClientRegisterDataById({ Id: itemId }),
        fetchDocumentRequiredOptions(itemId),
        fetchJobRequireOptions(itemId),
      ]);
      const row = response?.data || {};
      setFormData((prev) => ({
        ...prev,
        ...row,
        id: isCopyMode ? null : row?.id || row?._id || prev?.id,
        clientCompanyId: row?.clientCompanyId || row?.companyId || prev.clientCompanyId,
        companyId: row?.clientCompanyId || row?.companyId || prev.companyId,
        plan: row?.plan || row?.planId || prev.plan,
        planId: row?.plan || row?.planId || prev.planId,
        personalityTestLink:
          row?.personalityTestLink || row?.personalityLink || prev.personalityTestLink,
        recConName: row?.recConName || row?.recruiterName || prev.recConName,
        recruiterName: row?.recConName || row?.recruiterName || prev.recruiterName,
        contactName: row?.contactName || row?.recConName || row?.recruiterName || prev.contactName,
        recConEmail: row?.recConEmail || row?.recruiterEmail || prev.recConEmail,
        recruiterEmail: row?.recConEmail || row?.recruiterEmail || prev.recruiterEmail,
        contactEmail:
          row?.contactEmail || row?.recConEmail || row?.recruiterEmail || prev.contactEmail,
        recConPhone: row?.recConPhone || row?.recruiterPhone || prev.recConPhone,
        recruiterPhone: row?.recConPhone || row?.recruiterPhone || prev.recruiterPhone,
        contactPhone: toPhoneDigits(
          row?.contactPhone || row?.recConPhone || row?.recruiterPhone || prev.contactPhone
        ),
        billName: row?.billName || row?.billingName || prev.billName,
        billingName: row?.billName || row?.billingName || prev.billingName,
        billEmail: row?.billEmail || row?.billingEmail || prev.billEmail,
        billingEmail: row?.billEmail || row?.billingEmail || prev.billingEmail,
        billPhone: row?.billPhone || row?.billingPhone || prev.billPhone,
        billingPhone: toPhoneDigits(row?.billPhone || row?.billingPhone || prev.billingPhone),
        billAddress: row?.billAddress || row?.address || prev.billAddress,
        address: row?.billAddress || row?.address || prev.address,
        howManyRoutes: row?.howManyRoutes || row?.routeCount || prev.howManyRoutes,
        routeCount: row?.howManyRoutes || row?.routeCount || prev.routeCount,
        clientSourceId: row?.clientSourceId || row?.sourceId || prev.clientSourceId,
        clientBUcode: row?.clientBUcode || row?.clientBUCode || row?.bUcode || prev.clientBUcode,
        clientBUCode: row?.clientBUcode || row?.clientBUCode || row?.bUcode || prev.clientBUCode,
        facilityID: row?.facilityID || row?.facilityId || prev.facilityID,
        facilityId: row?.facilityID || row?.facilityId || prev.facilityId,
        twilioPhoneNumberId:
          row?.twilioNumberId ||
          row?.TwilioNumberId ||
          row?.twilioPhoneNumberId ||
          row?.twilioPhoneId ||
          prev.twilioPhoneNumberId,
        twilioPhoneId:
          row?.twilioNumberId ||
          row?.TwilioNumberId ||
          row?.twilioPhoneNumberId ||
          row?.twilioPhoneId ||
          prev.twilioPhoneId,
        fadvAccount: row?.fadvAccount || row?.accountNumber || prev.fadvAccount,
        accountNumber: row?.fadvAccount || row?.accountNumber || prev.accountNumber,
        fadvUser: row?.fadvUser || row?.loginId || prev.fadvUser,
        loginId: row?.fadvUser || row?.loginId || prev.loginId,
        fadvLoginId: row?.fadvLoginId || row?.fadvUser || row?.loginId || prev.fadvLoginId,
        fadvPassword: row?.fadvPassword || row?.password || prev.fadvPassword,
        password: row?.fadvPassword || row?.password || prev.password,
        fadvSecret: row?.fadvSecret || row?.secret || prev.fadvSecret,
        secret: row?.fadvSecret || row?.secret || prev.secret,
        sendEmails: Array.isArray(row?.sendEmails) ? row.sendEmails : prev.sendEmails,
        reportEmails: Array.isArray(row?.sendEmails)
          ? row.sendEmails.join(",")
          : row?.reportEmails || prev.reportEmails,
        emails: Array.isArray(row?.sendEmails)
          ? row.sendEmails.join(",")
          : row?.emails || row?.sendEmail || row?.reportEmails || prev.emails,
        selectedJobs: resolveJobIdsFromRow(row, prev.selectedJobs),
        jobIds: resolveJobIdsFromRow(row, prev.jobIds),
        inProgressMailInterval: parseIntervalValue(
          row?.inProgresMailInterval ?? row?.inProgressMailInterval ?? row?.inProgressInterval
        ),
        drugPassMailInterval: parseIntervalValue(row?.drugPassMailInterval ?? row?.drugPassInterval),
        complianceMailInterval: parseIntervalValue(row?.complianceMailInterval ?? row?.complianceInterval),
        clientReportMailInterval: parseIntervalValue(
          row?.clientReportMailInterval ?? row?.clientReportInterval
        ),
        reportsInProgress: parseIntervalValue(row?.reportSends).includes("InProgress"),
        reportsClientReport: parseIntervalValue(row?.reportSends).includes("ClientReport"),
        sendCandidateLink: Boolean(row?.sendCandidateLink ?? row?.candidateLink ?? prev.sendCandidateLink),
        incentiveTargetMin: row?.incentiveTargetMin ?? row?.targetMin ?? prev.incentiveTargetMin,
        incentiveTargetMax: row?.incentiveTargetMax ?? row?.targetMax ?? prev.incentiveTargetMax,
        incentivePerAmount: row?.incentivePerAmount ?? row?.perAmount ?? prev.incentivePerAmount,
        incentiveNegative: Boolean(row?.incentiveNegative ?? prev.incentiveNegative),
        sendSMS: Boolean(row?.sendSMS ?? row?.smsToClient ?? prev.sendSMS),
        sendSMSCandidate: Boolean(row?.sendSMSCandidate ?? row?.smsToCandidate ?? prev.sendSMSCandidate),
        smsToClient: Boolean(row?.sendSMS ?? row?.smsToClient ?? prev.smsToClient),
        smsToCandidate: Boolean(row?.sendSMSCandidate ?? row?.smsToCandidate ?? prev.smsToCandidate),
        isAIRecruiterEnabled: Boolean(
          row?.isAIRecruiterEnabled ??
          row?.aiRecruiterEnabled ??
          row?.smsSettings?.aiRecruiterEnabled ??
          prev.isAIRecruiterEnabled
        ),
        smsSettings: {
          ...prev.smsSettings,
          welcomeSms: Boolean(row?.sendWellcomeSMS ?? row?.welcomeSMS ?? prev.smsSettings.welcomeSms),
          fadvSms: Boolean(row?.sendFADVSMS ?? row?.fadvSms ?? prev.smsSettings.fadvSms),
          backgroundSms: Boolean(
            row?.sendBackgroundSMS ?? row?.backgroundSMS ?? prev.smsSettings.backgroundSms
          ),
          drugScreeningSms: Boolean(
            row?.sendDrugScreeningSMS ?? row?.drugScreeningSMS ?? prev.smsSettings.drugScreeningSms
          ),
          mecSms: Boolean(row?.sendMECSMS ?? row?.mecsms ?? prev.smsSettings.mecSms),
          aiRecruiterEnabled: Boolean(
            row?.isAIRecruiterEnabled ??
            row?.aiRecruiterEnabled ??
            row?.smsSettings?.aiRecruiterEnabled ??
            prev.smsSettings.aiRecruiterEnabled
          ),
        },
        documentRequired: buildDocumentRequiredFromApi(
          Array.isArray(row?.clientDocumentRequireds) && row.clientDocumentRequireds.length
            ? row.clientDocumentRequireds
            : apiRows
        ),
        specialRequirements: buildJobRequireFromApi(
          Array.isArray(row?.jobRequireDetails) && row.jobRequireDetails.length
            ? row.jobRequireDetails
            : jobRequireRows
        ),
      }));
      const selectedJobCategoryId = row?.jobCategoryId || row?.job_category_id || "";
      if (selectedJobCategoryId) {
        await fetchJobsByJobCategoryId(selectedJobCategoryId);
      }
      setErrors({});
      setFormKey((k) => k + 1);
      } finally {
        setDocumentRequiredLoading(false);
        setClientDataLoading(false);
      }
    };
    hydrateEditData();
  }, [itemId, isOpen, isCopyMode, fetchDocumentRequiredOptions, fetchJobRequireOptions]);

  const pageTitlePrefix = isViewMode
    ? "View"
    : isCopyMode
      ? "Copy"
      : itemId
        ? "Edit"
        : "Create";

  const fetchJobsByJobCategoryId = async (jobCategoryId) => {
    if (!jobCategoryId) {
      setJobOptions([]);
      return;
    }

    const jobRes = await fetchCommonJobOptions({
      hireStatus: null,
      jobCategoryId,
    });
    const rawJobs =
      jobRes?.data?.records ??
      jobRes?.data?.data ??
      jobRes?.data ??
      jobRes?.records ??
      [];

    const mappedJobs = (Array.isArray(rawJobs) ? rawJobs : [])
      .map((item) => ({
        value: String(item?.id || item?._id || item?.jobId || item?.value || item?.name || ""),
        label:
          item?.display ||
          item?.name ||
          item?.title ||
          item?.jobName ||
          item?.label ||
          "",
      }))
      .filter((item) => item?.value && item?.label);

    const dedupedMap = new Map();
    mappedJobs.forEach((item) => {
      dedupedMap.set(String(item.value), item);
    });
    setJobOptions(Array.from(dedupedMap.values()));
  };

  const staticJobOptions = useMemo(() => jobOptions || [], [jobOptions]);

  const loaderContent = <Loaders />;

  const formContent = (
    <Form onSubmit={handleSubmit}>
      <fieldset
        disabled={isViewMode}
        style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}
      >
      <div ref={formTopRef} />
      <h5 className="mb-3">Recruiting Contact</h5>
      <Row>
        <Col md={4}>
          <FormGroup>
            <Label>Job Category <span className="text-danger">*</span></Label>
            <div className={errors.jobCategoryId ? "is-invalid" : ""}>
              <Select
                options={jobCategoryOptions}
                className={`select2-selection${errors.jobCategoryId ? " is-invalid" : ""}`}
                placeholder="Select Job Category"
                isDisabled={isViewMode}
                value={getSelectedOption(jobCategoryOptions, formData.jobCategoryId)}
                onChange={(selectedOption) => {
                  const selectedJobCategoryId = selectedOption?.value || "";
                  setFormData((prev) => ({
                    ...prev,
                    jobCategoryId: selectedJobCategoryId,
                    selectedJobs: [],
                    jobIds: [],
                  }));
                  fetchJobsByJobCategoryId(selectedJobCategoryId);
                  if (errors?.jobCategoryId || errors?.selectedJobs) {
                    setErrors((prev) => ({ ...prev, jobCategoryId: "", selectedJobs: "" }));
                  }
                }}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="fixed"
                styles={selectZIndexStyles}
              />
            </div>
            {errors.jobCategoryId ? (
              <div className="invalid-feedback d-block">{errors.jobCategoryId}</div>
            ) : null}
          </FormGroup>
        </Col>
        <Col md={8}>
          <FormGroup>
            <Label>Job <span className="text-danger">*</span></Label>
            {formData?.jobCategoryId ? (
              <div
                className={`border rounded p-2${errors.selectedJobs ? " border-danger" : ""}`}
              >
                {(staticJobOptions || []).map((option) => (
                  <Label key={`${option.value}-${formKey}`} check className="me-2 mb-1">
                    <Input
                      key={`job-${option.value}-${formKey}`}
                      type="checkbox"
                      defaultChecked={selectedJobIdSet.has(String(option.value))}
                      onChange={() => {
                        const nextValue = String(option.value);
                        const selectedSet = new Set(toStringArray(formData.selectedJobs));
                        if (selectedSet.has(nextValue)) selectedSet.delete(nextValue);
                        else selectedSet.add(nextValue);
                        const nextJobs = Array.from(selectedSet);
                        setFormData((prev) => ({
                          ...prev,
                          selectedJobs: nextJobs,
                          jobIds: nextJobs,
                        }));
                        if (errors?.selectedJobs) {
                          setErrors((prev) => ({ ...prev, selectedJobs: "" }));
                        }
                      }}
                    />{" "}
                    {option.label}
                  </Label>
                ))}
              </div>
            ) : null}
            {errors.selectedJobs ? (
              <div className="invalid-feedback d-block">{errors.selectedJobs}</div>
            ) : null}
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md={4}>
          <FormGroup>
            <div
              className={`plan-change-tooltip-wrap${
                planChangeTooltipMessage ? " plan-change-tooltip-wrap--active" : ""
              }`}
            >
              {planChangeTooltipMessage ? (
                <span
                  className="plan-change-tooltip"
                  role="status"
                  aria-live="polite"
                >
                  {planChangeTooltipMessage}
                </span>
              ) : null}
              <Label>Plan</Label>
              <Select
                options={planOptions}
                className="select2-selection"
                placeholder="Select Plan"
                isDisabled={isViewMode}
                value={getSelectedOption(planOptions, formData.plan || formData.planId)}
                onChange={(selectedOption) => {
                  const selectedValue = selectedOption?.value || "";
                  const previousValue = formData.plan || formData.planId || "";
                  setFormData((prev) => ({
                    ...prev,
                    plan: selectedValue,
                    planId: selectedValue,
                    ...(selectedOption?.incentiveMin != null
                      ? { incentiveTargetMin: Number(selectedOption.incentiveMin) }
                      : {}),
                    ...(selectedOption?.incentiveMax != null
                      ? { incentiveTargetMax: Number(selectedOption.incentiveMax) }
                      : {}),
                  }));
                  if (String(selectedValue) !== String(previousValue)) {
                    showPlanChangeTooltip();
                  }
                }}
              />
            </div>
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup>
            <Label>Company <span className="text-danger">*</span></Label>
            <div className={errors.companyId ? "is-invalid" : ""}>
              <Select
                options={companyOptions}
                className={`select2-selection${errors.companyId ? " is-invalid" : ""}`}
                placeholder="Select Job Company"
                isDisabled={isViewMode}
                value={getSelectedOption(companyOptions, formData.clientCompanyId || formData.companyId)}
                onChange={(selectedOption) => {
                  const selectedValue = selectedOption?.value || "";
                  setFormData((prev) => ({
                    ...prev,
                    clientCompanyId: selectedValue,
                    companyId: selectedValue,
                  }));
                  if (errors?.companyId) {
                    setErrors((prev) => ({ ...prev, companyId: "" }));
                  }
                }}
              />
            </div>
            {errors.companyId ? (
              <div className="invalid-feedback d-block">{errors.companyId}</div>
            ) : null}
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup>
            <Label>Client Name <span className="text-danger">*</span></Label>
            <Input
              name="clientName"
              value={formData.clientName}
              onChange={handleInputChange}
              invalid={!!errors.clientName}
            />
            <FormFeedback>{errors.clientName}</FormFeedback>
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md={4}>
          <FormGroup>
            <Label>Interview Link</Label>
            <Select
              options={interviewLinkOptions}
              className="select2-selection"
              placeholder="Select Interview Link"
              isDisabled={isViewMode}
              value={getSelectedOption(interviewLinkOptions, formData.interviewLink)}
              onChange={(selectedOption) => updateFormField("interviewLink", selectedOption?.value || "")}
            />
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup>
            <Label>Name <span className="text-danger">*</span></Label>
            <Input
              name="contactName"
              value={formData.contactName}
              onChange={handleInputChange}
              invalid={!!errors.contactName}
            />
            <FormFeedback>{errors.contactName}</FormFeedback>
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup>
            <Label>Email <span className="text-danger">*</span></Label>
            <Input
              name="contactEmail"
              value={formData.contactEmail}
              onChange={handleInputChange}
              invalid={!!errors.contactEmail}
            />
            <FormFeedback>{errors.contactEmail}</FormFeedback>
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md={4}>
          <FormGroup>
            <Label>Phone <span className="text-danger">*</span></Label>
            <PatternFormat
              customInput={Input}
              format={US_PHONE_INPUT_FORMAT}
              allowEmptyFormatting
              name="contactPhone"
              type="text"
              inputMode="numeric"
              value={formData.contactPhone || ""}
              onValueChange={handleContactPhoneValueChange}
              onPaste={handlePhonePaste("contactPhone")}
              disabled={isViewMode}
              invalid={!!errors.contactPhone}
            />
            <FormFeedback>{errors.contactPhone}</FormFeedback>
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup>
            <Label>PersonalityTest Link</Label>
            <Select
              options={personalityOptions}
              className="select2-selection"
              placeholder="Select Personality Test Link"
              isDisabled={isViewMode}
              value={getSelectedOption(personalityOptions, formData.personalityTestLink)}
              onChange={(selectedOption) =>
                updateFormField("personalityTestLink", selectedOption?.value || "")
              }
            />
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup>
            <Label>TimeZone <span className="text-danger">*</span></Label>
            <div className={errors.timeZone ? "is-invalid" : ""}>
              <Select
                options={timezoneOptions}
                className={`select2-selection${errors.timeZone ? " is-invalid" : ""}`}
                placeholder="Select Time Zone"
                isDisabled={isViewMode}
                value={getSelectedOption(timezoneOptions, formData.timeZone)}
                onChange={(selectedOption) => updateFormField("timeZone", selectedOption?.value || "")}
              />
            </div>
            {errors.timeZone ? (
              <div className="invalid-feedback d-block">{errors.timeZone}</div>
            ) : null}
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md={4}>
          <FormGroup>
            <Label>Twilio Phone (optional)</Label>
            <Select
              options={twilioOptions}
              className="select2-selection"
              placeholder="Select Twilio Phone (optional)"
              isDisabled={isViewMode}
              value={getSelectedOption(
                twilioOptions,
                formData.twilioPhoneId || formData.twilioPhoneNumberId
              )}
              onChange={(selectedOption) => {
                const nextValue = selectedOption?.value || "";
                setFormData((prev) => ({
                  ...prev,
                  twilioPhoneId: nextValue,
                  twilioPhoneNumberId: nextValue,
                }));
              }}
            />
          </FormGroup>
        </Col>
      </Row>

      <h5 className="mt-4 mb-3">Billing Contact</h5>
      <Row>
        <Col md={4}>
          <FormGroup>
            <Label>Name <span className="text-danger">*</span></Label>
            <Input
              name="billingName"
              value={formData.billingName}
              onChange={handleInputChange}
              invalid={!!errors.billingName}
            />
            <FormFeedback>{errors.billingName}</FormFeedback>
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup>
            <Label>Email <span className="text-danger">*</span></Label>
            <Input
              name="billingEmail"
              value={formData.billingEmail}
              onChange={handleInputChange}
              invalid={!!errors.billingEmail}
            />
            <FormFeedback>{errors.billingEmail}</FormFeedback>
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup>
            <Label>Phone <span className="text-danger">*</span></Label>
            <PatternFormat
              customInput={Input}
              format={US_PHONE_INPUT_FORMAT}
              allowEmptyFormatting
              name="billingPhone"
              type="text"
              inputMode="numeric"
              value={formData.billingPhone || ""}
              onValueChange={handleBillingPhoneValueChange}
              onPaste={handlePhonePaste("billingPhone")}
              disabled={isViewMode}
              invalid={!!errors.billingPhone}
            />
            <FormFeedback>{errors.billingPhone}</FormFeedback>
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md={4}>
          <FormGroup>
            <Label>Entity ID <span className="text-danger">*</span></Label>
            <Input
              name="entityId"
              value={formData.entityId}
              onChange={handleInputChange}
              invalid={!!errors.entityId}
            />
            <FormFeedback>{errors.entityId}</FormFeedback>
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup>
            <Label>How many Routes do you own ? <span className="text-danger">*</span></Label>
            <Input
              name="howManyRoutes"
              value={formData.howManyRoutes}
              onChange={handleInputChange}
              invalid={!!errors.howManyRoutes}
            />
            <FormFeedback>{errors.howManyRoutes}</FormFeedback>
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <FormGroup>
            <Label>Address <span className="text-danger">*</span></Label>
            <Input
              type="textarea"
              rows={2}
              name="billAddress"
              value={formData.billAddress}
              onChange={handleInputChange}
              invalid={!!errors.billAddress}
            />
            <FormFeedback>{errors.billAddress}</FormFeedback>
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label>Terminal Address <span className="text-danger">*</span></Label>
            <Input
              type="textarea"
              rows={2}
              name="terminalAddress"
              value={formData.terminalAddress}
              onChange={handleInputChange}
              invalid={!!errors.terminalAddress}
            />
            <FormFeedback>{errors.terminalAddress}</FormFeedback>
          </FormGroup>
        </Col>
      </Row>

      <h5 className="mt-4 mb-3">First Advantage Login Details</h5>
      <Row>
        <Col md={6}>
          <FormGroup>
            <Label>Account number</Label>
            <Input name="accountNumber" value={formData.accountNumber} onChange={handleInputChange} />
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label>Login ID</Label>
            <Input name="fadvLoginId" value={formData.fadvLoginId} onChange={handleInputChange} />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <FormGroup>
            <Label>Password</Label>
            <Input name="password" value={formData.password} onChange={handleInputChange} />
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label>Secret</Label>
            <Input name="secret" value={formData.secret} onChange={handleInputChange} />
          </FormGroup>
        </Col>
      </Row>

      <h5 className="mt-4 mb-3">Email for send mail(multiple use comma separated(,))</h5>
      <Row>
        <Col md={12}>
          <FormGroup>
            <Label>Email's <span className="text-danger">*</span></Label>
            <Input
              name="emails"
              value={formData.emails}
              onChange={handleInputChange}
              invalid={!!errors.emails}
            />
            <FormFeedback>{errors.emails}</FormFeedback>
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <FormGroup>
            <Label>Hired onboarding From email (optional)</Label>
            <Input
              name="hiredOnboardingFromEmail"
              value={formData.hiredOnboardingFromEmail}
              onChange={handleInputChange}
            />
            <small className="text-muted">
              Optional. Used as the From address for automated onboarding emails when a candidate is marked
              Hired.
            </small>
          </FormGroup>
        </Col>
      </Row>

      <h5 className="mt-4 mb-3">Client source and BU code</h5>
      <Row>
        <Col md={4}>
          <FormGroup>
            <Label>Client source <span className="text-danger">*</span></Label>
            <div className={errors.clientSourceId ? "is-invalid" : ""}>
              <Select
                options={sourceOptions}
                className={`select2-selection${errors.clientSourceId ? " is-invalid" : ""}`}
                placeholder="Select Client Source"
                isDisabled={isViewMode}
                value={getSelectedOption(sourceOptions, formData.clientSourceId)}
                onChange={(selectedOption) =>
                  updateFormField("clientSourceId", selectedOption?.value || "")
                }
              />
            </div>
            {errors.clientSourceId ? (
              <div className="invalid-feedback d-block">{errors.clientSourceId}</div>
            ) : null}
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup>
            <Label>ClientBUcode</Label>
            <Input
              name="clientBUcode"
              value={formData.clientBUcode}
              onChange={handleInputChange}
              invalid={!!errors.clientBUcode}
            />
            <FormFeedback>{errors.clientBUcode}</FormFeedback>
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup>
            <Label>FacilityID <span className="text-danger">*</span></Label>
            <Input
              name="facilityId"
              value={formData.facilityId}
              onChange={handleInputChange}
              invalid={!!errors.facilityId}
            />
            <FormFeedback>{errors.facilityId}</FormFeedback>
          </FormGroup>
        </Col>
      </Row>

      <h5 className="mt-4 mb-3">Client Settings</h5>
      <Row className="mb-2">
        <Col md={4}>
          <Label className="fw-semibold">Reports send <span className="text-danger">*</span></Label>
          <div>
            <Label check className="me-2">
              <Input
                key={`reportsInProgress-${formKey}`}
                type="checkbox"
                defaultChecked={formData.reportsInProgress}
                onChange={(e) => {
                  updateFormField("reportsInProgress", e.target.checked);
                  if (errors?.reportSends) {
                    setErrors((prev) => ({ ...prev, reportSends: "" }));
                  }
                }}
              />{" "}
              In Progress
            </Label>
            <Label check>
              <Input
                key={`reportsClientReport-${formKey}`}
                type="checkbox"
                defaultChecked={formData.reportsClientReport}
                onChange={(e) => {
                  updateFormField("reportsClientReport", e.target.checked);
                  if (errors?.reportSends) {
                    setErrors((prev) => ({ ...prev, reportSends: "" }));
                  }
                }}
              />{" "}
              Client Report
            </Label>
          </div>
          {errors.reportSends ? (
            <div className="invalid-feedback d-block">{errors.reportSends}</div>
          ) : null}
        </Col>
        <Col md={4}>
          <div className="mt-4">
            <Label check className="me-2">
              <Input
                key={`smsToClient-${formKey}`}
                type="checkbox"
                defaultChecked={formData.smsToClient}
                onChange={(e) => updateFormField("smsToClient", e.target.checked)}
              />{" "}
              SMS To Client?
            </Label>
            <Label check>
              <Input
                key={`smsToCandidate-${formKey}`}
                type="checkbox"
                defaultChecked={formData.smsToCandidate}
                onChange={(e) => updateFormField("smsToCandidate", e.target.checked)}
              />{" "}
              SMS To Candidate?
            </Label>
          </div>
        </Col>
        <Col md={4}>
          <div className="mt-4">
            <Label check>
              <Input
                key={`sendCandidateLink-${formKey}`}
                type="checkbox"
                defaultChecked={formData.sendCandidateLink}
                onChange={(e) => updateFormField("sendCandidateLink", e.target.checked)}
              />{" "}
              Send Candidate Link ?
            </Label>
          </div>
        </Col>
      </Row>

      <Row>
        <Col md={4}>
          <Label className="fw-semibold">In-Progres Mail Interval</Label>
          <div>{WEEK_DAYS.map((day) => (
            <Label key={`inprog-${day}-${formKey}`} check className="me-2">
              <Input
                type="checkbox"
                defaultChecked={formData.inProgressMailInterval.includes(day)}
                onChange={() => handleDayToggle("inProgressMailInterval", day)}
              />{" "}
              {day}
            </Label>
          ))}</div>
        </Col>
        <Col md={4}>
          <Label className="fw-semibold">Drug Pass Mail Interval</Label>
          <div>{WEEK_DAYS.map((day) => (
            <Label key={`drug-${day}-${formKey}`} check className="me-2">
              <Input
                type="checkbox"
                defaultChecked={formData.drugPassMailInterval.includes(day)}
                onChange={() => handleDayToggle("drugPassMailInterval", day)}
              />{" "}
              {day}
            </Label>
          ))}</div>
        </Col>
        <Col md={4}>
          <Label className="fw-semibold">Compliance Mail Interval</Label>
          <div>{WEEK_DAYS.map((day) => (
            <Label key={`comp-${day}-${formKey}`} check className="me-2">
              <Input
                type="checkbox"
                defaultChecked={formData.complianceMailInterval.includes(day)}
                onChange={() => handleDayToggle("complianceMailInterval", day)}
              />{" "}
              {day}
            </Label>
          ))}</div>
        </Col>
      </Row>
      <Row className="mt-2">
        <Col md={4}>
          <Label className="fw-semibold">Client Report Mail Interval</Label>
          <div>{WEEK_DAYS.map((day) => (
            <Label key={`clientrep-${day}-${formKey}`} check className="me-2">
              <Input
                type="checkbox"
                defaultChecked={formData.clientReportMailInterval.includes(day)}
                onChange={() => handleDayToggle("clientReportMailInterval", day)}
              />{" "}
              {day}
            </Label>
          ))}</div>
        </Col>
      </Row>

      <h5 className="mt-4 mb-3">Document Required</h5>
      {!(formData.documentRequired || []).length ? (
        <p className="text-muted small mb-3">No document required options found.</p>
      ) : null}
      <Table bordered responsive>
        <tbody>
          {formData.documentRequired.map((item, index) => (
            <tr key={`${item.documentId || item.id}-${formKey}`}>
              <td style={{ width: "70%" }}>
                <Label check>
                  <Input
                    key={`doc-${item.documentId}-${formKey}`}
                    type="checkbox"
                    defaultChecked={item.isActive}
                    onChange={() => toggleDocumentRequired(index)}
                  />{" "}
                  {item.documentName}
                </Label>
              </td>
              <td>
                <Input
                  type="file"
                  onChange={(e) => handleDocumentFileChange(index, e.target.files?.[0] || null)}
                />
                {item?.existingFileUrl ? (
                  <div className="mt-1">
                    <a
                      href={item.existingFileUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="small fw-bold d-inline-flex align-items-center"
                    >
                      <i className="mdi mdi-download me-1" />
                      Download existing document
                    </a>
                  </div>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <h5 className="mt-4 mb-3">SMS Settings</h5>
      <Row>
        {[
          ["welcomeSms", "Send Wellcome SMS ?"],
          ["fadvSms", "Send FADV SMS ?"],
          ["backgroundSms", "Send Background SMS ?"],
          ["drugScreeningSms", "Send DrugScreening SMS ?"],
          ["mecSms", "Send MEC SMS ?"],
          ["aiRecruiterEnabled", "Is AI Recruiter Enabled"],
        ].map(([key, label]) => (
          <Col md={3} key={`${key}-${formKey}`} className="mb-2">
            <Label check>
              <Input
                key={`smsSetting-${key}-${formKey}`}
                type="checkbox"
                defaultChecked={Boolean(formData.smsSettings?.[key])}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    ...(key === "aiRecruiterEnabled"
                      ? { isAIRecruiterEnabled: e.target.checked }
                      : {}),
                    smsSettings: {
                      ...prev.smsSettings,
                      [key]: e.target.checked,
                    },
                  }))
                }
              />{" "}
              {label}
            </Label>
          </Col>
        ))}
      </Row>

      <h5 className="mt-4 mb-3">Special Requirements</h5>
      {!(formData.specialRequirements || []).length ? (
        <p className="text-muted small mb-3">No special requirements found.</p>
      ) : null}
      <Table bordered responsive>
        <thead>
          <tr>
            <th>Type</th>
            <th style={{ width: "90px" }}>Tool</th>
            <th style={{ width: "90px" }}>Client</th>
            <th style={{ width: "90px" }}>Optional</th>
          </tr>
        </thead>
        <tbody>
          {formData.specialRequirements.map((row, index) => (
            <tr key={`${row.type}-${formKey}`}>
              <td>{row.type}</td>
              <td>
                <Input
                  key={`${row.type}-tool-${formKey}`}
                  type="checkbox"
                  defaultChecked={row.tool}
                  onChange={() => toggleSpecialRequirement(index, "tool")}
                />
              </td>
              <td>
                <Input
                  key={`${row.type}-client-${formKey}`}
                  type="checkbox"
                  defaultChecked={row.client}
                  onChange={() => toggleSpecialRequirement(index, "client")}
                />
              </td>
              <td>
                <Input
                  key={`${row.type}-optional-${formKey}`}
                  type="checkbox"
                  defaultChecked={row.optional}
                  onChange={() => toggleSpecialRequirement(index, "optional")}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {!isViewMode ? (
        <div className="mt-4 text-end">
          <Button color="primary" type="submit" disabled={submitting}>
            {submitting ? "Submitting..." : "Submit"}
          </Button>
        </div>
      ) : null}
      </fieldset>
    </Form>
  );

  if (!isModalMode) {
    return (
      <div className="page-content client-register-add-edit">
        <Container fluid>
          <Card className="border mb-3">
            <CardBody key={formKey}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="fw-bold mb-0">
                  {pageTitlePrefix} Client Register
                </h5>
                <Button color="light" onClick={closeModal}>Back</Button>
              </div>
              <hr />
              {clientDataLoading || interviewLinkLoading ? loaderContent : formContent}
            </CardBody>
          </Card>
        </Container>
      </div>
    );
  }

  return (
    <Modal
    isOpen={companyAddEditModalOpen}
    toggle={closeModal}
      backdrop="static"
      className="modal-full-height client-register-add-edit"
      size="xl"
      centered
    >
      <ModalBody key={formKey} className="p-4" style={{ maxHeight: "95vh", overflowY: "auto" }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="fw-bold mb-0">
            {pageTitlePrefix} Client Register
          </h5>
          <Button close onClick={closeModal} />
        </div>
        <hr />
        {clientDataLoading || interviewLinkLoading ? loaderContent : formContent}
      </ModalBody>
    </Modal>
  );
};

export default ClientRegisterAddEditModal;
